import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { CreateRentChangeInput } from "../../lib/realestate";
import { Modal } from "../ui/Modal";

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: CreateRentChangeInput) => Promise<void>;
}

function parseEuroDe(input: string): number {
  const trimmed = input.replace(/\./g, "").replace(",", ".").trim();
  const n = Number.parseFloat(trimmed);
  if (Number.isNaN(n) || n < 0) throw new Error("invalid_amount");
  return Math.round(n * 100);
}

export function RentChangeFormModal({ open, onClose, onSubmit }: Props) {
  const { t } = useTranslation();
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [coldRent, setColdRent] = useState("");
  const [sideCosts, setSideCosts] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    setError(null);
    try {
      const input: CreateRentChangeInput = {
        effectiveFrom,
        coldRentCents: parseEuroDe(coldRent),
        sideCostsAdvanceCents: sideCosts.trim() ? parseEuroDe(sideCosts) : 0,
      };
      if (notes.trim()) input.notes = notes.trim();
      setSubmitting(true);
      await onSubmit(input);
      setEffectiveFrom("");
      setColdRent("");
      setSideCosts("");
      setNotes("");
      onClose();
    } catch {
      setError(t("realestate.errors.invalid_amount"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={t("realestate.rent_change.add_title")}>
      <div className="space-y-3">
        <label className="block">
          <span className="text-sm">{t("realestate.rent_change.effective_from")}</span>
          <input
            type="date"
            className="block mt-1 w-full border rounded px-2 py-1"
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)}
            required
          />
        </label>
        <label className="block">
          <span className="text-sm">{t("realestate.rent_change.cold_rent")}</span>
          <input
            inputMode="decimal"
            className="block mt-1 w-full border rounded px-2 py-1"
            value={coldRent}
            onChange={(e) => setColdRent(e.target.value)}
            placeholder="1.050,00"
            required
          />
        </label>
        <label className="block">
          <span className="text-sm">{t("realestate.rent_change.side_costs_advance")}</span>
          <input
            inputMode="decimal"
            className="block mt-1 w-full border rounded px-2 py-1"
            value={sideCosts}
            onChange={(e) => setSideCosts(e.target.value)}
            placeholder="220,00"
          />
        </label>
        <label className="block">
          <span className="text-sm">{t("realestate.rent_change.notes")}</span>
          <textarea
            className="block mt-1 w-full border rounded px-2 py-1"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={500}
            rows={2}
            placeholder="z.B. Indexmiete +2,5 %"
          />
        </label>
        {error && <div className="text-red-700 text-sm">{error}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1 border rounded">
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-3 py-1 bg-blue-600 text-white rounded"
          >
            {submitting ? t("common.submitting") : t("common.save")}
          </button>
        </div>
      </div>
    </Modal>
  );
}
